/**
 * Three-card intro, shown once on first open and re-openable from Profile. Purely local
 * state — the flag lives in localStorage, so it costs no request and survives a reload.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Media } from '../components/Media';
import { Button } from '../components/primitives';
import { useI18n } from '../lib/i18n';
import { haptic } from '../hooks/useTg';
import { useChrome } from '../hooks/useTg';

const ONBOARDING_KEY = 'pv.onboarded';

export function hasOnboarded(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === '1';
  } catch {
    return true; // private mode: never trap the user on the intro
  }
}

function markOnboarded() {
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* private mode */
  }
}

const SLIDES = [
  {
    seed: 'welcome-1',
    en: {
      title: 'Prompts that actually work',
      body: 'Every prompt ships with the example output it produced — and the parameters that got it there.',
    },
    fa: {
      title: 'پرامپت‌هایی که واقعاً کار می‌کنند',
      body: 'هر پرامپت با نمونه خروجی و تنظیماتی که به آن رسیده منتشر می‌شود.',
    },
  },
  {
    seed: 'welcome-2',
    en: {
      title: 'Copy, run, rate',
      body: 'One tap copies the full prompt to your clipboard. Rate what worked so the next person finds it faster.',
    },
    fa: {
      title: 'کپی کن، اجرا کن، امتیاز بده',
      body: 'با یک لمس، کل پرامپت کپی می‌شود. به آنچه جواب داده امتیاز بدهید تا نفر بعد سریع‌تر پیدایش کند.',
    },
  },
  {
    seed: 'welcome-3',
    en: {
      title: 'Built for Telegram',
      body: 'No account, no download. Your favourites and collections follow your Telegram profile.',
    },
    fa: {
      title: 'ساخته‌شده برای تلگرام',
      body: 'بدون حساب کاربری و بدون دانلود. علاقه‌مندی‌ها و مجموعه‌های شما با پروفایل تلگرام‌تان همراه است.',
    },
  },
];

export function Welcome() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [step, setStep] = useState(0);

  useChrome({ header: 'surface', bottomBar: 'surface' });

  const slide = SLIDES[step]!;
  const copy = slide[lang];
  const last = step === SLIDES.length - 1;

  const finish = () => {
    markOnboarded();
    navigate('/', { replace: true });
  };

  return (
    <div className="flex min-h-full flex-col bg-surface">
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <Media seed={slide.seed} ratio="auto" className="h-full w-full" priority variant={step} />
        <button
          type="button"
          onClick={() => {
            haptic('light');
            finish();
          }}
          className="absolute end-4 top-4 rounded-full px-3 py-1.5 text-subhead text-on-media"
          style={{ background: 'var(--pv-media-scrim)' }}
        >
          {t.skip}
        </button>
      </div>

      <div
        className="flex flex-none flex-col items-center gap-3 px-6 pt-6 text-center"
        style={{ paddingBottom: 'calc(var(--pv-space-6) + var(--pv-safe-bottom))' }}
      >
        <div className="flex gap-1.5" aria-hidden="true">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className="h-[7px] rounded-full transition-[width,background] duration-200 ease-pv"
              style={{
                width: i === step ? 20 : 7,
                background: i === step ? 'var(--pv-accent)' : 'var(--pv-track-off)',
              }}
            />
          ))}
        </div>
        <h1 className="text-large-title font-bold text-text">{copy.title}</h1>
        <p className="text-balance text-callout leading-relaxed text-hint">{copy.body}</p>
        <Button
          size="lg"
          className="mt-1.5 w-full"
          onClick={() => (last ? finish() : setStep((s) => s + 1))}
        >
          {last ? t.startBrowsing : t.next}
        </Button>
      </div>
    </div>
  );
}
